import React, { useState, useRef, useEffect, memo } from "react";
import { IconChevronDown, IconChevronUp, IconSearch, IconPlus } from "@tabler/icons-react";

export interface DropdownOption {
  value: string | number;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: DropdownOption[];
  selectedValues: string[];
  onChange: (newSelectedValues: string[]) => void;
  hasSearch?: boolean;
  allowCustom?: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
}

/**
 * Reusable multi-select dropdown with checkboxes.
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  hasSearch = false,
  allowCustom = false,
  onSync,
  isSyncing = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [customOptions, setCustomOptions] = useState<DropdownOption[]>(() => {
    if (!allowCustom) return [];
    return selectedValues
      .filter((v) => v !== "all" && !options.some((opt) => String(opt.value) === v))
      .map((v) => ({ value: v, label: v }));
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const allOptions: DropdownOption[] = [...options, ...customOptions];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchText("");
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (open) {
      setSearchText("");
    }
  };

  const handleCheckboxChange = (value: string) => {
    if (value === "all") {
      if (selectedValues.includes("all")) {
        onChange([]);
      } else {
        onChange(["all"]);
      }
      return;
    }

    let newSelected: string[];
    if (selectedValues.includes(value)) {
      newSelected = selectedValues.filter((v) => v !== value);
    } else {
      newSelected = [...selectedValues.filter((v) => v !== "all"), value];
    }

    onChange(newSelected);
  };

  const handleAddCustom = () => {
    const value = searchText.trim();
    if (!value) return;

    const exists = allOptions.some(
      (opt) => String(opt.value) === value || opt.label.toLowerCase() === value.toLowerCase()
    );

    if (!exists) {
      setCustomOptions((prev) => [...prev, { value, label: value }]);
    }

    const newSelected = [...selectedValues.filter((v) => v !== "all"), value];
    const unique = [...new Set(newSelected)];
    onChange(unique);
    setSearchText("");
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && allowCustom) {
      e.preventDefault();
      handleAddCustom();
    }
  };

  const filteredOptions =
    hasSearch && searchText
      ? allOptions.filter((opt) => opt.label.toLowerCase().includes(searchText.toLowerCase()))
      : allOptions;

  const getDisplayText = (): string => {
    if (selectedValues.includes("all") || selectedValues.length === 0) {
      return label;
    }
    if (selectedValues.length === 1) {
      const selected = allOptions.find((opt) => String(opt.value) === selectedValues[0]);
      return selected ? selected.label : selectedValues[0];
    }
    return `${selectedValues.length} đã chọn`;
  };

  return (
    <div className="position-relative" ref={containerRef} style={{ minWidth: "160px" }}>
      <div
        className="form-control text-start d-flex align-items-center justify-content-between cursor-pointer px-3"
        onClick={handleToggle}
        style={{ userSelect: "none" }}
      >
        <span className="text-truncate me-2">{getDisplayText()}</span>
        {open ? <IconChevronUp size={16} className="text-secondary flex-shrink-0" /> : <IconChevronDown size={16} className="text-secondary flex-shrink-0" />}
      </div>

      {open && (
        <div
          className="dropdown-menu show position-absolute mt-1 shadow-sm border p-2"
          style={{ width: "100%", minWidth: "220px", maxHeight: "280px", overflowY: "auto", zIndex: 1050 }}
        >
          {hasSearch && (
            <div className="input-icon mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Tìm kiếm phiên bản..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              />
              <span className="input-icon-addon">
                <IconSearch size={14} />
              </span>
            </div>
          )}

          <div className="d-flex flex-column gap-1">
            {filteredOptions.map((option) => {
              const isChecked = selectedValues.includes(String(option.value));

              return (
                <label
                  key={String(option.value)}
                  className="dropdown-item d-flex align-items-center gap-2 rounded px-2 py-1 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={isChecked}
                    onChange={() => handleCheckboxChange(String(option.value))}
                  />
                  <span className="text-truncate">{option.label}</span>
                </label>
              );
            })}

            {filteredOptions.length === 0 && !allowCustom && (
              <div className="text-muted small text-center py-2">
                Không tìm thấy kết quả
              </div>
            )}

            {allowCustom && searchText.trim() && !allOptions.some((opt) => opt.label.toLowerCase() === searchText.trim().toLowerCase()) && (
              <div
                className="dropdown-item d-flex align-items-center gap-2 text-primary cursor-pointer rounded px-2 py-1 border-top mt-1"
                onClick={handleAddCustom}
              >
                <IconPlus size={14} />
                <span className="fw-medium">Tìm theo phiên bản "{searchText.trim()}"</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(MultiSelectDropdown);
